import React, { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { PDFDocument } from 'pdf-lib';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist/build/pdf';
import workerSrc from 'pdfjs-dist/build/pdf.worker.entry';
import { FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import _ from 'lodash';
import './DigiSignatureTools.css';
import CustomSignatureCanvas from './componenets/CustomSignatureCanvas';
import { useTranslate } from '../../hooks';
import GenerateSign from './componenets/GenerateSign';
import CustomAddButton from '../components/CustomAddButton/CustomAddButton';
import { Button, FileInput } from '../../components';
import { DigitalSignature } from '../../api/services/digitalSignatureService';
import { useAppSelector } from '../../infra/hooks';
import toast from 'react-hot-toast';
import ModalFooter from '../Modals/CustomModal/ModalFooter';

GlobalWorkerOptions.workerSrc = workerSrc;

const DigiSignatureTools = (props: any) => {
    const [pdfFile, setPdfFile] = useState<any>(null);
    const [pdfData, setPdfData] = useState(null)
    const [previewFile, setPreviewFile] = useState<any>(null);
    const [signatureUrl, setSignatureUrl] = useState<any>(null);
    const [clickBox, setClickBox] = useState<any>(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalCount, setTotalCount] = useState(1);
    const [selectedType, setSelectedType] = useState(1)
    const [isLoading, setIsLoading] = useState(false);
    const [canvasDims, setCanvasDims] = useState({ cssW: 0, cssH: 0, pxW: 0, pxH: 0 });

    const sigPadRef = useRef<any>();
    const canvasRef = useRef<any>();
    const { translate } = useTranslate()
    const selector = useAppSelector((state) => state.authSlice);

    const [signature, setSignature] = useState(null);
    const [signatureData, setSignatureData] = useState(null);

    const [customWidth, setWidth] = useState<any>()
    const [customHeight, setHeight] = useState<any>()

    const uploadFile = (file: any) => {
        if (file) {
            const preview: any = URL.createObjectURL(file);
            setPdfFile(file);
            setPdfData(file);
            setPreviewFile(preview);
        } else {
            setPdfFile(null);
            setPdfData(null);
            setPreviewFile(null);
        }
    };

    useEffect(() => {
        if (props?.attachment) {
            setPdfData(props?.attachment)
            setPdfFile(props?.attachment)
        }
    }, [props?.attachment])

    const uploadSignature = (file: any) => {
        if (file) {
            const fileReader: any = new FileReader();
            fileReader.onloadend = () => {
                setSignature(file);
                setSignatureData(file);
                setSignatureUrl(fileReader.result);
            };
            fileReader.readAsDataURL(file);
        } else {
            setSignature(null);
            setSignatureData(null);
        }
    };

    useEffect(() => {
        return () => {
            if (previewFile?.startsWith('blob:')) {
                URL.revokeObjectURL(previewFile);
            }
        };
    }, [previewFile]);

    async function getImageDataAndType(url: any) {
        const response = await fetch(url);
        const buffer = await response.arrayBuffer();
        const contentType = response.headers.get('content-type');

        return { buffer, contentType };
    }

    const placeSignatureAndDownload = async () => {
        if (!pdfData || !signatureUrl || !clickBox) {
            alert('Missing PDF, signature or placement.');
            return;
        }

        let pdfBytes;
        if (typeof pdfData === 'string') {
            const response = await fetch(pdfData);
            const blob = await response.blob();
            pdfBytes = await blob.arrayBuffer();
        } else {
            pdfBytes = await pdfFile.arrayBuffer();
        }

        const pdfDoc = await PDFDocument.load(pdfBytes);
        let pngImage: any;
        if (selectedType === 3) {
            const { buffer, contentType } = await getImageDataAndType(signatureUrl);
            if (contentType === 'image/png') {
                pngImage = await pdfDoc.embedPng(buffer);
            } else if (contentType === 'image/jpeg') {
                pngImage = await pdfDoc.embedJpg(buffer);
            } else {
                toast.error('Unsupported image format.');
            }
        } else pngImage = await pdfDoc.embedPng(signatureUrl);
        const page = pdfDoc.getPages()[pageNumber - 1];
        const { width: pdfW, height: pdfH } = page.getSize();

        // Canvas DOM & sizes
        const canvasEl = canvasRef.current;
        const cssW = canvasDims.cssW || canvasEl?.clientWidth || 1;   // CSS pixels
        const cssH = canvasDims.cssH || canvasEl?.clientHeight || 1;
        const pxW = canvasDims.pxW || canvasEl?.width || cssW;      // device pixels (drawing buffer)
        const pxH = canvasDims.pxH || canvasEl?.height || cssH;

        // Convert selection box from CSS → canvas pixels
        const sx = pxW / cssW;
        const sy = pxH / cssH;

        const boxCanvasX = clickBox.x * sx;
        const boxCanvasY = clickBox.y * sy;
        const boxCanvasW = clickBox.width * sx;
        const boxCanvasH = clickBox.height * sy;

        // Convert from canvas pixels → PDF units, flipping Y
        const x = (boxCanvasX / pxW) * pdfW;
        const y = pdfH - ((boxCanvasY + boxCanvasH) / pxH) * pdfH;
        const w = (boxCanvasW / pxW) * pdfW;
        const h = (boxCanvasH / pxH) * pdfH;

        // (Optional) guard rails to stay on-page
        const clamp = (val: any, min: any, max: any) => Math.max(min, Math.min(max, val));
        const finalX = clamp(x, 0, Math.max(0, pdfW - w));
        const finalY = clamp(y, 0, Math.max(0, pdfH - h));

        page.drawImage(pngImage, {
            x: finalX,
            y: finalY,
            width: w,
            height: h,
        });

        const signedPdfBytes: any = await pdfDoc.save();
        const blob = new Blob([signedPdfBytes], { type: 'application/pdf' });
        const file = new File([blob], 'signed-document.pdf', { type: 'application/pdf' });
        console.log('Upload file:', file);

        const formData = new FormData();
        formData.append('event', 'digiSignDocs');
        formData.append('attachment', file);
        setIsLoading(true);
        DigitalSignature.uploadSignature(formData, selector?.company_id)
            .then((res: any) => {
                setIsLoading(false);
                props?.setAttachment(res?.data)
            })
            .catch((err: any) => {
                setIsLoading(false);
                toast.error(translate('Something went wrong, please try again later.'));
                console.error('Upload failed:', err);
            });

        // Optionally open preview
        // const previewUrl = URL.createObjectURL(blob);
        // window.open(previewUrl, '_blank');
    };

    useEffect(() => {
        const renderPdfToCanvas = async () => {
            if (!pdfData || !canvasRef.current) return;

            try {
                const response = await fetch(pdfData);
                const pdfBytes = await response.arrayBuffer(); // fetch URL as arrayBuffer

                const loadingTask = getDocument({ data: pdfBytes });
                const pdf = await loadingTask.promise;

                const totalPages = pdf.numPages;
                setTotalCount(totalPages);

                const page = await pdf.getPage(pageNumber);
                const viewport = page.getViewport({ scale: 1.5 });

                const canvas: any = canvasRef.current;
                const context = canvas.getContext('2d');

                canvas.width = viewport.width;
                canvas.height = viewport.height;

                const renderContext = {
                    canvasContext: context,
                    viewport: viewport,
                };

                await page.render(renderContext).promise;

                setCanvasDims({
                    cssW: canvas.clientWidth || viewport.width,
                    cssH: canvas.clientHeight || viewport.height,
                    pxW: canvas.width,
                    pxH: canvas.height,
                });
            } catch (err) {
                console.error('Error rendering PDF:', err);
            }
        };

        renderPdfToCanvas();
    }, [pdfData, pageNumber, props?.attachment]);

    const signType = [
        { id: 1, name: 'DrawSign' },
        { id: 2, name: 'TextSign' },
        { id: 3, name: 'UploadSign' }
    ];

    const changeConditions = (event: any) => {
        setSignatureUrl(null);
        setSignature(null);
        setSignatureData(null);
        setSelectedType(Number(event.target.value));
    };

    return (
        <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr', gap: '0.5rem' }}>
                {pdfData && (
                    <div style={{ boxSizing: 'border-box', border: '0.1em solid grey', borderRadius: '10px', padding: '10px', width: '650px' }}>
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '600px', margin: '0.5rem 0' }}>
                                <CustomAddButton
                                    btnLabel={translate('Previous')}
                                    onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                                    disabled={pageNumber === 1}
                                    icon={''}
                                />
                                <CustomAddButton
                                    btnLabel={translate('Next')}
                                    onClick={() => setPageNumber((prev) => prev + 1)}
                                    disabled={pageNumber === totalCount}
                                    icon={''}
                                />
                            </div>
                            <CustomSignatureCanvas
                                ref={canvasRef}
                                signatureURL={signatureUrl}
                                onSelectBox={setClickBox}
                                onWidth={setWidth}
                                onHeight={setHeight}
                            />
                        </div>
                    </div>
                )}
                <div>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            maxWidth: selectedType === 3 ? '600px' : '800px',
                            maxHeight: '300px',
                            boxSizing: 'border-box',
                            border: '0.1em solid grey',
                            borderRadius: '10px',
                            padding: '10px',
                        }}
                    >
                        <div style={{ width: '200px' }}>
                            <FormControl>
                                <RadioGroup
                                    aria-labelledby="demo-radio-buttons-group-label"
                                    name="radio-buttons-group"
                                    value={selectedType}
                                    onChange={changeConditions}
                                >
                                    {_.map(signType, function (signType) {
                                        return (
                                            <div key={signType.id} style={{ display: 'flex', alignItems: 'center' }}>
                                                <FormControlLabel
                                                    className="label"
                                                    sx={{
                                                        paddingX: 1,
                                                        '& .MuiFormControlLabel-label': {
                                                            fontSize: '0.7rem',
                                                        },
                                                    }}
                                                    value={signType.id}
                                                    defaultValue={selectedType}
                                                    control={
                                                        <Radio
                                                            size="small"
                                                            sx={{
                                                                color: '#7C0E6F',
                                                                '&.Mui-checked': {
                                                                    color: '#7C0E6F',
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    label={signType.name}
                                                />
                                            </div>
                                        );
                                    })}
                                </RadioGroup>
                            </FormControl>
                        </div>
                        {selectedType === 1 && (
                            <div style={{ display: 'grid', rowGap: '0.5rem' }}>
                                <SignatureCanvas
                                    penColor="black"
                                    canvasProps={{
                                        width: 600,
                                        height: 200,
                                        style: { border: '1px solid black' },
                                    }}
                                    ref={sigPadRef}
                                    onEnd={() => {
                                        const sigData = sigPadRef.current.getCanvas().toDataURL('image/png');
                                        setSignatureUrl(sigData);
                                    }}
                                />
                                <div>
                                    <Button
                                        onClick={() => {
                                            setSignatureUrl(null);
                                            sigPadRef.current.clear();
                                        }}
                                        className="primary-btn"
                                    >
                                        {translate('Clear')}
                                    </Button>
                                </div>
                            </div>
                        )}
                        {selectedType === 2 && (
                            <GenerateSign
                                customWidth={customWidth}
                                customHeight={customHeight}
                                signatureUrl={setSignatureUrl}
                            />
                        )}
                        {selectedType === 3 && (
                            <div>
                                <FileInput
                                    name=""
                                    files={signature}
                                    handleFiles={uploadSignature}
                                    className="my-3"
                                />
                            </div>
                        )}
                    </div>
                    <div style={{ margin: '0.5rem 0', display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                        <ModalFooter
                            isSubmitting={isLoading}
                            onClickSubmit={() => placeSignatureAndDownload()}
                            title_for_submit_btn={'Upload SIgned PDF'}
                        />
                    </div>
                </div>
            </div>
        </div>

    );
};

export default DigiSignatureTools;
